// React & Helper Libraries
import { memo, useEffect, useState } from "react";
import { useTranslate } from "helpers";

// Services & API Calls
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import {
  createVehicleGroupAPI,
  getVehicleGroupAPI,
  getVehicleGroupsAPI,
  updateVehicleGroupAPI,
} from "services/api/calculations/vehiclegroup/api";
import {
  VehicleGroupEditFormValues,
  VehicleGroupRow,
} from "services/api/calculations/vehiclegroup/types";

//types
import { VehicleGroupEditProps } from "./types";

// UI Components
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import {
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";

// Constants
import { CALCULATION_VEHICLE_GROUP_EDIT_FORM_SCHEMA } from "./validation";
import { initialVehicleGroupEditForm } from "services/api/calculations/vehiclegroup/consts";

const VehicleGroupEditCard: React.FC<VehicleGroupEditProps> = (props) => {
  const { t } = useTranslate();
  const { vehicleGroupId, onSuccess } = props;
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<VehicleGroupEditFormValues>(
    initialVehicleGroupEditForm
  );

  const [calculationVehicleGroupParents, setVehicleGroupParents] = useState<
    DataWithPagination<VehicleGroupRow>
  >(initialDataWithPagination);

  useEffect(() => {
    fetchVehicleGroupParent(
      calculationVehicleGroupParents.pagination,
      vehicleGroupId
    );
    if (vehicleGroupId) {
      fetchVehicleGroup(vehicleGroupId);
    }
  }, []);

  const fetchVehicleGroup = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialVehicleGroupEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getVehicleGroupAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchVehicleGroupParent = async (
    pagination: Pagination,
    vehicleGroupId?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getVehicleGroupsAPI(pagination, "", vehicleGroupId);

      setVehicleGroupParents(results);
    } catch {
      setVehicleGroupParents(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CALCULATION_VEHICLE_GROUP_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!vehicleGroupId) {
        result = await updateVehicleGroupAPI(vehicleGroupId, formValues);
      } else {
        result = await createVehicleGroupAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const vehicleGroupItems: DropdownOption[] =
    calculationVehicleGroupParents.data
      .filter((i) => i.id !== vehicleGroupId) // Filtrira element s ID 123
      .map((i) => ({
        value: i.id.toString(),
        text: i.name,
      }));

  return (
    <>
      <div className="flex gap-4">
        <div className="h-screen w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="name"
                      label="Naziv"
                      placeholder={t(
                        "admin.calculation.setting.vehiclegroup.add.name"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.name}
                      value={formValues.name}
                      required
                      endIcon=""
                    ></TextField>
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <Dropdown
                      label={t(
                        "admin.calculation.setting.vehiclegroup.add.parentvehiclegroup"
                      )}
                      placeholder={t(
                        "admin.calculation.setting.vehiclegroup.add.parentvehiclegroup.placeholder"
                      )}
                      name="parentVehicleGroupId"
                      value={formValues.parentVehicleGroupId}
                      options={vehicleGroupItems}
                      onChange={handleDropdownChange}
                    />
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(VehicleGroupEditCard);
